import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { fetchFarm, fetchThresholds } from "@/lib/api";
import { fdiLevel, fmt, LEVEL_LABELS, RISK_COLORS, levelRank } from "@/lib/risk";
import { RiskBar } from "@/components/RiskBar";
import StatusBadge from "@/components/StatusBadge";
import { fdiPosition } from "@/lib/risk";

export default function FirePage() {
    const { data } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm, refetchInterval: 60_000 });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });

    const ranked = useMemo(() => {
        if (!data || !thr) return [];
        return [...data.stations]
            .map((s) => ({ ...s, level: fdiLevel(s.current.fdi, thr.fdi) }))
            .sort((a, b) => levelRank(b.level) - levelRank(a.level) || b.current.fdi - a.current.fdi);
    }, [data, thr]);

    if (!data || !thr) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const chartData = ranked.map((s) => ({ name: s.name, fdi: s.current.fdi, level: s.level }));
    const worst = ranked[0];

    return (
        <div className="space-y-6" data-testid="fire-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Harvest Fire Risk</h1>
                    <p className="text-sm text-slate-500 mt-1">Harvest Fire Danger Index (HFDI) — Agbyte methodology · shutdown threshold HFDI 35</p>
                </div>
                <div className="fw-card px-4 py-3 flex items-center gap-3" data-testid="farm-hfdi-max">
                    <Flame className="h-5 w-5" style={{ color: RISK_COLORS[worst.level].hex }} />
                    <div>
                        <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Farm HFDI</div>
                        <div className="font-bold text-slate-900">{fmt(worst.current.fdi, 0)} · Highest: {worst.name}</div>
                        {data?.farm?.fdi_avg !== undefined && (
                            <div className="text-[10px] text-slate-500 fw-metric">Avg {fmt(data.farm.fdi_avg, 0)}</div>
                        )}
                    </div>
                    <StatusBadge level={worst.level} />
                </div>
            </div>

            <div className="fw-card p-5">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>HFDI by station</h2>
                    <span className="text-[11px] font-semibold text-slate-500 fw-metric">Shutdown ≥ 35</span>
                </div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 13, fontWeight: 600 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }} />
                            <Bar dataKey="fdi" radius={[6, 6, 0, 0]}>
                                {chartData.map((d, i) => (
                                    <Cell key={i} fill={RISK_COLORS[d.level].hex} />
                                ))}
                                <LabelList dataKey="fdi" position="top" formatter={(v) => fmt(v, 0)} style={{ fill: "#0F172A", fontWeight: 700 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ranked.map((s) => (
                    <Link key={s.id} to={`/stations/${s.id}`} data-testid={`fire-station-${s.id}`}
                        className="fw-card fw-card-hover p-5 block">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Station</div>
                                <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.name}</div>
                            </div>
                            <StatusBadge level={s.level} label={`${LEVEL_LABELS[s.level]} Risk`} />
                        </div>
                        <div className="mt-3">
                            <div className="flex items-baseline justify-between mb-1">
                                <span className="fw-metric text-3xl font-extrabold text-slate-900">HFDI {fmt(s.current.fdi, 0)}</span>
                                <span className="text-xs text-slate-500">Rain 24h {fmt(s.rain_24h, 1)}mm · 7d {fmt(s.rain_7d, 1)}mm</span>
                            </div>
                            <RiskBar level={s.level} position={fdiPosition(s.current.fdi, thr.fdi)} label="HFDI" />
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                            <MiniStat label="Temp" value={`${fmt(s.current.temp, 1)}°`} />
                            <MiniStat label="RH" value={`${fmt(s.current.humidity, 0)}%`} />
                            <MiniStat label="Wind" value={`${fmt(s.current.wind, 1)}`} />
                            <MiniStat label="Gust" value={`${fmt(s.current.gust, 1)}`} />
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="bg-slate-50 rounded-lg py-2">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
            <div className="fw-metric font-bold text-slate-900">{value}</div>
        </div>
    );
}
